import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { cn } from "../lib/cn";

// Stamp's mask without its tilt; on the inner face so the focus outline paints at full strength.
const stampFace = cn(
  "inline-flex items-center justify-center gap-2 rounded-[2px] border-2 bg-transparent px-3.5 py-1.5",
  "font-mono text-[0.6875rem] font-medium uppercase tracking-[0.13em]",
  "[mask-image:radial-gradient(ellipse_at_center,black_60%,rgba(0,0,0,0.72)_100%)]",
  "transition duration-150 ease-paper motion-reduce:transition-none",
);

export interface ConfirmDialogProps extends Omit<
  React.DialogHTMLAttributes<HTMLDialogElement>,
  "open" | "title" | "children" | "onClose" | "onCancel"
> {
  open: boolean;
  /** Escape, backdrop, close button and the cancel action all land here. */
  onCancel: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use when the change is hard to undo. */
  destructive?: boolean;
  /** Both buttons go inert and the clipboard stops dismissing. */
  busy?: boolean;
  confirmTestId?: string;
  cancelTestId?: string;
  ref?: React.Ref<HTMLDialogElement>;
}

/** Native props land on the `<dialog>`, which stays mounted while closed. */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  confirmTestId,
  cancelTestId,
  ...rest
}: ConfirmDialogProps) {
  return (
    <Dialog
      {...rest}
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      dismissable={!busy}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid={cancelTestId}
          >
            {cancelLabel}
          </Button>
          {destructive ? (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              aria-busy={busy || undefined}
              data-testid={confirmTestId}
              className={cn(
                "group inline-flex select-none items-center justify-center rounded-[2px] bg-transparent",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                "disabled:cursor-not-allowed",
              )}
            >
              <span
                className={cn(
                  stampFace,
                  busy
                    ? "border-faint text-faint"
                    : "border-ink-stopped text-ink-stopped",
                )}
              >
                {busy && (
                  <>
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-3.5 shrink-0 animate-spin rounded-full",
                        "border-[1.5px] border-current border-t-transparent",
                        "motion-reduce:hidden",
                      )}
                    />
                    <span
                      aria-hidden="true"
                      className="hidden font-mono leading-none motion-reduce:inline"
                    >
                      ...
                    </span>
                  </>
                )}
                {confirmLabel}
              </span>
            </button>
          ) : (
            <Button
              variant="primary"
              onClick={onConfirm}
              busy={busy}
              data-testid={confirmTestId}
            >
              {confirmLabel}
            </Button>
          )}
        </>
      }
    >
      {children}
    </Dialog>
  );
}
